import { createTheme } from "@mui/material/styles";

// ========================================
// UFind Default Theme Color Palette
// ========================================
// This is the official default theme (Light mode + Green accent)
// extracted from Home and related components.
// Settings: Theme=Light, Accent=Green, Language=Auto-detect
// ========================================

export const ufindDefaultColors = {
  // Primary green shades (main brand color)
  primary: {
    main: "#516d4f",          // Main green - text, icons, borders
    dark: "#344b35",          // Dark green - buttons, headers
    darker: "#2e4630",        // Darker green - headings, strong text
    darkest: "#2a3c2a",       // Darkest green - button hover states
    light: "#526D50",         // Light green variant
    lighter: "#A0C49D",       // Lighter green - icons, accents
  },
  
  // Background colors
  background: {
    default: "#FBFFE9",       // Main page background (cream/light yellow)
    paper: "#FFFFFF",         // Card/paper background
    searchBar: "#e1ecc8",     // Search bar background (light green)
    tag: "#E8F3E0",           // Tags, badges background
    hover: "#f4f4f4",         // Hover state background
  },
  
  // Text colors
  text: {
    primary: "#2e4630",       // Primary text (dark green)
    secondary: "#516d4f",     // Secondary text (medium green)
    label: "#516d4f",         // Form labels
    placeholder: "#516d4f",   // Input placeholders
    disabled: "#a0a0a0",      // Disabled text
  },
  
  // Status/feedback colors
  status: {
    error: "#c74f4f",         // Error messages, warnings
    white: "#ffffff",         // White text on dark backgrounds
  },
  
  // Border colors
  border: {
    default: "#ccc",          // Standard borders
    light: "#E8F3E0",         // Light borders (cards)
    divider: "#f0f0f0",       // Dividers
  },
  
  // Shadow colors (rgba values preserved for transparency)
  shadow: {
    card: "rgba(0, 0, 0, 0.05)",      // Card shadow
    cardHover: "rgba(0, 0, 0, 0.1)",  // Card hover shadow
    cardHoverStrong: "rgba(0, 0, 0, 0.15)", // Strong card hover
    button: "rgba(81, 109, 79, 0.12)",     // Button shadow (green tinted)
    buttonHover: "rgba(81, 109, 79, 0.25)", // Button hover shadow
    overlay: "rgba(81, 109, 79, 0.4)",     // Overlay shadow
  },
  
  // Interactive states
  interactive: {
    primaryHover: "rgba(81, 109, 79, 0.05)", // Primary color hover overlay
    disabled: "#cccccc",      // Disabled button background
  },
};

// ========================================
// Complete UFind Color Registry
// ========================================
// Comprehensive palette extracted from all pages and components
// Organized by application section: Shared, User, GSO, Admin, Auth
// ========================================

export const ufindColorRegistry = {
  // ----------------------------------------
  // SHARED COLORS (Used across multiple sections)
  // ----------------------------------------
  shared: {
    // Main brand greens
    green: {
      main: "#516d4f",           // Primary brand green
      mainAlt: "#526D50",        // Main variant
      dark: "#344b35",           // Dark green
      darker: "#2e4630",         // Darker green
      darkest: "#2a3c2a",        // Darkest green (hover states)
      darkerAlt: "#3e523d",      // Alternative dark
      light: "#A0C49D",          // Light green (icons, accents)
      lighter: "#a0c49d",        // Lighter green variant
      lightAlt: "#8ab287",       // Light green alternative
      lightAlt2: "#8bb174",      // Another light variant
      lightAlt3: "#a5c38e",      // Third light variant
      pastel: "#aac093",         // Pastel green
      hover: "#8ab887",          // Hover state
    },
    
    // Background shades
    background: {
      cream: "#FBFFE9",          // Main cream background
      creamAlt: "#fbffe8",       // Cream variant
      white: "#FFFFFF",          // Pure white
      whiteAlt: "#fff",          // White variant
      lightGreen: "#E1ECC8",     // Light green background
      lightGreenAlt: "#e1ecc8",  // Light green variant
      lightGreenAlt2: "#d0e7ba", // Another light green
      lightGreenAlt3: "#E8F3E0", // Pale green
      lightGreenAlt4: "#d1dfb5", // Light green hover
      lightYellow: "#f7fbe8",    // Light yellow tint
      hover: "#f4f4f4",          // Hover background
      hoverAlt: "#f0f7e9",       // Alternative hover
      hoverAlt2: "#f4f9ec",      // Another hover variant
      gray: "#f1f1f1",           // Light gray
      grayAlt: "#e0e0e0",        // Gray variant
      grayAlt2: "#d0d0d0",       // Darker gray
      greenTint: "#f1f8f1",      // Green-tinted background
      greenPale: "#e5f0dc",      // Pale green background
    },
    
    // Text colors
    text: {
      primary: "#2e4630",        // Primary dark green text
      secondary: "#516d4f",      // Secondary green text
      label: "#526D50",          // Form labels
      muted: "#607d3b",          // Muted green
      disabled: "#a0a0a0",       // Disabled text
      gray: "#999",              // Gray text
      black: "#000",             // Black (rarely used)
    },
    
    // Borders
    border: {
      default: "#ccc",           // Standard border
      light: "#E8F3E0",          // Light green border
      lightAlt: "#e1ecc8",       // Light green alternative
      lightAlt2: "#d0e7ba",      // Another light border
      lightAlt3: "#c7dfb7",      // Pale green border
      divider: "#f0f0f0",        // Divider lines
    },
    
    // Shadows (rgba preserved)
    shadow: {
      light: "rgba(0, 0, 0, 0.05)",
      medium: "rgba(0, 0, 0, 0.1)",
      mediumStrong: "rgba(0, 0, 0, 0.15)",
      strong: "rgba(0, 0, 0, 0.25)",
      greenLight: "rgba(81, 109, 79, 0.08)",
      greenMedium: "rgba(81, 109, 79, 0.12)",
      greenStrong: "rgba(81, 109, 79, 0.4)",
      greenStronger: "rgba(81, 109, 79, 0.5)",
      greenButton: "rgba(81, 109, 79, 0.3)",
      greenButtonHover: "rgba(81, 109, 79, 0.4)",
      overlay: "rgba(0,0,0,0.25)",
      overlayDark: "rgba(0,0,0,0.5)",
      overlayGreen: "rgba(81, 109, 79, 0.6)",
    },
    
    // Status colors
    status: {
      error: "#c74f4f",          // Error red
      errorAlt: "#ff5858",       // Error red variant
      success: "#2e7d32",        // Success green
      successAlt: "#4caf50",     // Success green variant
      white: "#ffffff",          // White text
      whiteAlt: "#fbffe8",       // Off-white
    },
  },

  // ----------------------------------------
  // USER SECTION COLORS
  // ----------------------------------------
  user: {
    // AboutUs specific
    aboutUs: {
      sectionBg: "#e1ecc8",      // Section background
      sectionText: "#516d4f",    // Section text
      primaryBg: "#516d4f",      // Primary section background
      primaryText: "#fbffe8",    // Primary section text
      scrollButton: "#a0c49d",   // Scroll to top button
      scrollButtonHover: "#8ab887", // Scroll button hover
      iconWhite: "#fff",         // White icon
    },
    
    // Profile specific
    profile: {
      cardBg: "#ffff",           // Card background
      badge: "#e1ecc8",          // Verification badge border
      badgeBg: "#f7fbe8",        // Badge background
    },
    
    // Settings specific
    settings: {
      shadow: "rgba(0,0,0,0.12)", // Box shadow
      tabBg: "#e1ecc8",          // Tab background
      contentBg: "#ffff",        // Content background
      sidebarBorder: "#e0e0e0",  // Sidebar border
      sidebarBg: "#e1ecc8",      // Sidebar background
      borderDivider: "#f0f0f0",  // Divider border
    },
    
    // Tutorial specific
    tutorial: {
      pageBg: "#fbffe8",         // Page background
      headerText: "#516d4f",     // Header text
      cardBg: "#e1ecc8",         // Card background
      sectionText: "#516d4f",    // Section text
    },
    
    // Result page specific
    result: {
      pageBg: "#FBFFE9",         // Page background
      backButton: "#344b35",     // Back button
      backButtonHover: "#516d4f", // Back button hover
      heading: "#2e4630",        // Heading text
      cardBg: "#f4f9ec",         // Card background
      cardBorder: "#c7dfb7",     // Card border
      itemName: "#344b35",       // Item name
      detailText: "#607d3b",     // Detail text
      claimButton: "#344b35",    // Claim button
      claimButtonHover: "#e5f0dc", // Claim hover background
      claimBorderHover: "#516d4f", // Claim border hover
      errorText: "#c74f4f",      // Error text
    },
    
    // Header specific
    header: {
      bg: "#fbffe8",             // Header background
      menuIcon: "#516d4f",       // Menu icon
      profileBg: "#e1ecc8",      // Profile button background (active)
      profileText: "#516d4f",    // Profile text
    },
    
    // Notification overlay
    notification: {
      bg: "#e1ecc8",             // Notification background
      shadow: "rgba(0, 0, 0, 0.1)", // Box shadow
      headerText: "#516d4f",     // Header text
      hoverBg: "#d1dfb5",        // Item hover background
      itemTitle: "#516d4f",      // Item title
      itemText: "#516d4f",       // Item text
      emptyText: "#999",         // Empty state text
    },
    
    // Avatar overlay (ProfilePopup)
    avatarOverlay: {
      errorText: "#c74f4f",      // Error text
      logoutBg: "#e1ecc8",       // Logout button background
      shadow: "rgba(81, 109, 79, 0.4)", // Box shadow main
      shadowAlt: "rgba(81, 109, 79, 0.3)", // Box shadow secondary
      hoverBg: "#d1dfb5",        // Logout hover background
      loadingColor: "#c74f4f",   // Loading spinner
      menuItemColor: "#516d4f",  // Menu item color
    },
    
    // Report Found Item
    reportFoundItem: {
      pageBg: "#FBFFE9",         // Page background
      cardBg: "#E1ECC8",         // Card background
      headingText: "#526D50",    // Heading text
      fieldBg: "#FBFFE9",        // Input field background
    },
    
    // Processing view
    processing: {
      pageBg: "#FBFFE9",         // Page background
      textColor: "#516d4f",      // Loading text
    },
  },

  // ----------------------------------------
  // GSO SECTION COLORS
  // ----------------------------------------
  gso: {
    // Found Items specific
    foundItems: {
      pageBg: "#FBFFE9",         // Page background
      cardBg: "#E1ECC8",         // Main card background
      tableBg: "#FBFFE9",        // Table background
      tableBorder: "#d0e7ba",    // Table border
      headerText: "#526D50",     // Header text
      rowHoverBg: "#f0f7e9",     // Row hover background
      modalBg: "#fff",           // Modal background
      detailsBg: "#FBFFE9",      // Details background
      statusBadge: "#526D50",    // Status badge background
      statusText: "#FBFFE9",     // Status badge text
      scrollbarTrack: "#E1ECC8", // Scrollbar track
      scrollbarThumb: "#526D50", // Scrollbar thumb
      buttonBg: "#526D50",       // Button background
      buttonHover: "#3e523d",    // Button hover
      spinner: "#526D50",        // Loading spinner
      exportButton: "#E1ECC8",   // Export button background
      exportHover: "#526D50",    // Export button hover
      exportText: "#3e523d",     // Export button hover text
    },
    
    // Most Common Lost Items
    mostCommonLostItems: {
      tableBg: "#fbffe8",        // Table background
      tableBorder: "#516d4f",    // Table border
      headerText: "#516d4f",     // Header text
      chartBg: "#fbffe8",        // Chart background
      chartBorder: "#516d4f",    // Chart border
      chartColors: ["#516d4f", "#8bb174", "#d0e7ba", "#e1ecc8", "#a5c38e"], // Pie chart colors
      strokeWhite: "#fff",       // Chart stroke
      legendBg: "#fbffe8",       // Legend background
      cardBg: "#e1ecc8",         // Card background
      cardShadow: "rgba(0, 0, 0, 0.25)", // Card shadow
      overlayBg: "rgba(81, 109, 79, 0.6)", // Overlay background
      searchBg: "#516d4f",       // Search bar background
      searchIcon: "#516d4f",     // Search icon
      menuIcon: "#516d4f",       // Menu icon
      detailText: "#516d4f",     // Detail text
      legendItemBg: "#516d4f",   // Legend item background
      legendBorder: "#516d4f",   // Legend border
    },
    
    // Heatmap
    heatmap: {
      pageBg: "#FBFFE9",         // Page background
      cardBg: "#E1ECC8",         // Card background
      defaultColor: "#3357FF",   // Default heatmap color
      mapBg: "#FBFFE9",          // Map background
    },
    
    // Header
    header: {
      bg: "#fbffe8",             // Header background
      text: "#516d4f",           // Text color
      sortButton: "#aac093",     // Sort button background
      sortIcon: "#526D50",       // Sort icon
    },
    
    // Sidebar
    sidebar: {
      bg: "#FBFFE9",             // Sidebar background
      shadow: "rgba(81, 109, 79, 0.12)", // Box shadow
      logoText: "#516d4f",       // Logo text
      sectionText: "#516d4f",    // Section text
      itemSelected: "rgba(81, 109, 79, 0.08)", // Selected item
      itemHover: "rgba(81, 109, 79, 0.12)", // Hover item
      iconColor: "#516d4f",      // Icon color
    },
    
    // Avatar Overlay
    avatarOverlay: {
      errorText: "#c74f4f",      // Error text
      logoutBg: "#e1ecc8",       // Logout background
      shadow: "rgba(0, 0, 0, 0.1)", // Box shadow
      hoverBg: "#d1dfb5",        // Hover background
      loadingColor: "#c74f4f",   // Loading spinner
      menuItemColor: "#516d4f",  // Menu item color
    },
    
    // Notification Overlay
    notification: {
      bg: "#e1ecc8",             // Notification background
      shadow: "rgba(0, 0, 0, 0.1)", // Box shadow
      headerText: "#516d4f",     // Header text
      hoverBg: "#d1dfb5",        // Item hover
      itemTitle: "#516d4f",      // Item title
      itemText: "#516d4f",       // Item text
      emptyText: "#999",         // Empty text
    },
  },

  // ----------------------------------------
  // ADMIN SECTION COLORS
  // ----------------------------------------
  admin: {
    // Dashboard
    dashboard: {
      bg: "#181a20",             // Main background
      text: "#fff",              // Main text
      cardBg: "#20232a",         // Card background
      cardBorder: "#23272f",     // Card border
      cardShadow: "rgba(0, 0, 0, 0.25)", // Card shadow
      
      // Feedback toast
      successGradientStart: "#43e97b", // Success gradient start
      successGradientEnd: "#38f9d7",   // Success gradient end
      errorGradientStart: "#ff5858",   // Error gradient start
      errorGradientEnd: "#f09819",     // Error gradient end
      feedbackText: "#181a20",         // Feedback text
      feedbackShadow: "rgba(0, 0, 0, 0.5)", // Feedback shadow
      closeButton: "#181a20",          // Close button
      
      // Tabs
      tabActiveGradientStart: "#1976d2", // Active tab gradient start
      tabActiveGradientEnd: "#2196f3",   // Active tab gradient end
      tabActiveText: "#fff",             // Active tab text
      tabActiveShadow: "rgba(25, 118, 210, 0.33)", // Active tab shadow
      tabInactiveText: "#aaa",           // Inactive tab text
      tabInactiveBorder: "#23272f",      // Inactive tab border
      
      // Search input
      searchBg: "#23272f",       // Search background
      searchBorder: "#333",      // Search border
      searchText: "#fff",        // Search text
      searchShadow: "rgba(0, 0, 0, 0.13)", // Search shadow
      
      // Buttons
      buttonGradientStart: "#1976d2", // Primary button gradient start
      buttonGradientEnd: "#2196f3",   // Primary button gradient end
      buttonText: "#fff",             // Button text
      buttonShadow: "rgba(25, 118, 210, 0.33)", // Button shadow
      
      // Table
      tableBg: "#23272f",        // Table background
      tableBorder: "#23272f",    // Table border
      tableShadow: "rgba(0, 0, 0, 0.13)", // Table shadow
      headerBg: "#23272f",       // Table header background
      headerText: "#90caf9",     // Table header text
      headerBorder: "#333",      // Header border
      rowBg: "rgba(25, 118, 210, 0.03)", // Row background
      rowBorder: "#222",         // Row border
      statusActive: "#4caf50",   // Active status
      statusInactive: "#f44336", // Inactive status
      loadingText: "#90caf9",    // Loading text
      errorText: "#f44336",      // Error text
      emptyText: "#90caf9",      // Empty state text
      
      // Action buttons
      resetGradientStart: "#1976d2", // Reset button gradient start
      resetGradientEnd: "#2196f3",   // Reset button gradient end
      resetShadow: "rgba(25, 118, 210, 0.33)", // Reset button shadow
      enableBg: "#4caf50",           // Enable button
      disableBg: "#23272f",          // Disable button background
      disableText: "#ffb300",        // Disable button text
      disableBorder: "#ffb300",      // Disable border
      deleteGradientStart: "#b71c1c", // Delete button gradient start
      deleteGradientEnd: "#f44336",   // Delete button gradient end
      deleteShadow: "rgba(183, 28, 28, 0.33)", // Delete shadow
      roleButtonBg: "#23272f",       // Role button background
      roleButtonText: "#fff",        // Role button text
      roleButtonBorder: "#1976d2",   // Role button border
      
      // Dialogs/Modals
      overlayBg: "rgba(0,0,0,0.5)", // Overlay background
      dialogBg: "#23272f",           // Dialog background
      dialogText: "#fff",            // Dialog text
      dialogShadow: "rgba(0, 0, 0, 0.5)", // Dialog shadow
      inputBg: "#181a20",            // Input background
      inputBorder: "#1976d2",        // Input border
      inputText: "#fff",             // Input text
      errorInputText: "#ff5858",     // Error input text
      cancelBg: "#23272f",           // Cancel button background
      cancelText: "#aaa",            // Cancel button text
      cancelBorder: "#444",          // Cancel border
    },
    
    // Header
    header: {
      bg: "#222",                // Header background
      text: "#fff",              // Header text
      logoutGradientStart: "#b71c1c", // Logout gradient start
      logoutGradientEnd: "#f44336",   // Logout gradient end
      logoutText: "#fff",             // Logout text
      dialogOverlay: "rgba(0,0,0,0.5)", // Dialog overlay
      dialogBg: "#23272f",            // Dialog background
      dialogText: "#fff",             // Dialog text
      dialogShadow: "rgba(0, 0, 0, 0.5)", // Dialog shadow
      confirmGradientStart: "#b71c1c", // Confirm button gradient start
      confirmGradientEnd: "#f44336",   // Confirm button gradient end
    },
  },

  // ----------------------------------------
  // AUTH SECTION COLORS (Login/Signup/Auth)
  // ----------------------------------------
  auth: {
    // Auth.jsx (combined login/signup with tabs)
    auth: {
      pageBg: "#FBFFE9",         // Page background
      cardBg: "#FBFFE9",         // Card background
      tabActiveBg: "#a0c49d",    // Active tab background
      tabActiveText: "#516d4f",  // Active tab text
      tabInactiveText: "#a0c49d", // Inactive tab text
      inputBorder: "#a0c49d",    // Input border
      inputText: "#516d4f",      // Input text
      inputFocusBackground: "rgba(160, 196, 157, 0.1)", // Input focus bg
      inputHoverBorder: "#8ab287", // Input hover border
      inputHoverBg: "#f1f8f1",   // Input hover background
      linkText: "#516d4f",       // Link text
      linkHover: "#8ab287",      // Link hover
      helperText: "#2e7d32",     // Helper text
      googleBorder: "#a0c49d",   // Google button border
      googleText: "#516d4f",     // Google button text
      googleBorderHover: "#8ab287", // Google hover border
      googleBgHover: "#f1f1f1",  // Google hover background
      buttonBg: "#a0c49d",       // Primary button background
      buttonLoadingBg: "#8ab287", // Button loading background
      buttonHover: "#8ab287",    // Button hover
      buttonText: "#fbffe8",     // Button text
      googleIconYellow: "#FBBC05", // Google icon yellow
      googleIconRed: "#EA4335",    // Google icon red
      googleIconGreen: "#34A853",  // Google icon green
      googleIconBlue: "#4285F4",   // Google icon blue
      resetModalBg: "#FBFFE9",   // Reset modal background
      resetHeaderText: "#516d4f", // Reset header text
      resetLabelText: "#516d4f",  // Reset label text
      resetInputBorder: "#a0c49d", // Reset input border
      resetHelperText: "#2e7d32", // Reset helper text
      resetBackBg: "#e0e0e0",    // Reset back button
      resetBackText: "#516d4f",  // Reset back text
      resetBackHover: "#d0d0d0", // Reset back hover
      resetButtonBg: "#a0c49d",  // Reset button background
      resetButtonLoadingBg: "#8ab287", // Reset loading background
      resetButtonText: "#fbffe8", // Reset button text
      resetButtonHover: "#8ab287", // Reset hover
      dropShadow: "rgba(0,0,0,0.1)", // Drop shadow
    },
    
    // Login.jsx (separate login page)
    login: {
      pageBg: "#FBFFE9",         // Page background
      cardBg: "#FBFFE9",         // Card background
      headingText: "#516d4f",    // Heading text
      inputBorder: "#a0c49d",    // Input border
      helperText: "#2e7d32",     // Helper text
      linkText: "#516d4f",       // Link text
      linkHover: "#8ab287",      // Link hover
      inputHoverBorder: "#8ab287", // Input hover border
      inputHoverBg: "#f1f8f1",   // Input hover background
      buttonBg: "#a0c49d",       // Button background
      buttonLoadingBg: "#8ab287", // Button loading
      buttonHover: "#8ab287",    // Button hover
      buttonText: "#fbffe8",     // Button text
      loadingSpinner: "#fbffe8", // Loading spinner
      googleBorder: "#a0c49d",   // Google button border
      googleText: "#516d4f",     // Google text
      googleBg: "#fff",          // Google background
      googleBorderHover: "#8ab287", // Google hover border
      googleBgHover: "#f1f1f1",  // Google hover background
      googleIconYellow: "#FBBC05", // Google icon colors
      googleIconRed: "#EA4335",
      googleIconGreen: "#34A853",
      googleIconBlue: "#4285F4",
      signupPromptText: "#516d4f", // Signup prompt text
      signupLinkText: "#516d4f",   // Signup link
      resetModalBg: "#FBFFE9",   // Reset modal background
      resetHeaderText: "#516d4f", // Reset header
      resetLabelText: "#516d4f",  // Reset label
      resetInputBorder: "#a0c49d", // Reset input border
      resetHelperText: "#2e7d32", // Reset helper text
      resetBackBg: "#e0e0e0",    // Reset back button
      resetBackText: "#516d4f",  // Reset back text
      resetBackHover: "#d0d0d0", // Reset back hover
      resetButtonBg: "#a0c49d",  // Reset button
      resetButtonLoadingBg: "#8ab287", // Reset loading
      resetButtonText: "#fbffe8", // Reset button text
      resetButtonHover: "#8ab287", // Reset hover
    },
    
    // Signup.jsx (separate signup page)
    signup: {
      pageBg: "#FBFFE9",         // Page background
      cardBg: "#FBFFE9",         // Card background
      headingText: "#516d4f",    // Heading text
      inputBorder: "#a0c49d",    // Input border
      buttonBg: "#a0c49d",       // Button background
      buttonLoadingBg: "#8ab287", // Button loading
      buttonHover: "#8ab287",    // Button hover
      buttonText: "#fbffe8",     // Button text
      googleBorder: "#a0c49d",   // Google border
      googleText: "#516d4f",     // Google text
      googleBg: "#fff",          // Google background
      googleBorderHover: "#8ab287", // Google hover border
      googleBgHover: "#f1f1f1",  // Google hover background
      googleIconYellow: "#FBBC05", // Google icons
      googleIconRed: "#EA4335",
      googleIconGreen: "#34A853",
      googleIconBlue: "#4285F4",
      loginPromptText: "#516d4f", // Login prompt text
      loginLinkText: "#516d4f",   // Login link
    },
  },

  // ----------------------------------------
  // COMPONENT-SPECIFIC COLORS
  // ----------------------------------------
  components: {
    // SearchBar
    searchBar: {
      bg: "#e1ecc8",             // Background
      iconColor: "#516d4f",      // Icon color
      closeButton: "#344b35",    // Close button
      closeButtonHover: "#2a3c2a", // Close hover
      closeText: "#ffffff",      // Close text
      textColor: "#516d4f",      // Text color
      errorIcon: "#c74f4f",      // Error icon (image without text)
      submitIcon: "#516d4f",     // Submit icon
      submitDisabled: "#a0a0a0", // Submit disabled
      disabledCursor: "#a0a0a0", // Disabled cursor
      errorText: "#c74f4f",      // Error text
    },
    
    // ProcessingView
    processing: {
      textColor: "#516d4f",      // Loading text
    },
    
    // ClaimForm
    claimForm: {
      shadow: "rgba(0, 0, 0, 0.05)", // Box shadow
      bg: "#FFFFFF",             // Background
      border: "#E8F3E0",         // Border
      inputBorder: "#516d4f",    // Input border
      headingText: "#2e4630",    // Heading text
      labelText: "#516d4f",      // Label text
      uploadText: "#516d4f",     // Upload label
      uploadButton: "#344b35",   // Upload button
      termsText: "#516d4f",      // Terms text
      cancelBorder: "#516d4f",   // Cancel border
      cancelText: "#516d4f",     // Cancel text
      cancelHoverBorder: "#344b35", // Cancel hover border
      cancelHoverBg: "rgba(81, 109, 79, 0.05)", // Cancel hover bg
      submitBg: "#344b35",       // Submit background
      submitText: "#ffffff",     // Submit text
      submitHover: "#2a3c2a",    // Submit hover
      submitDisabled: "#cccccc", // Submit disabled
    },
    
    // MatchCard
    matchCard: {
      shadow: "rgba(0, 0, 0, 0.05)", // Card shadow
      shadowHover: "rgba(0, 0, 0, 0.1)", // Hover shadow
      shadowHoverStrong: "rgba(0, 0, 0, 0.15)", // Strong hover shadow
      bg: "#FFFFFF",             // Card background
      border: "#E8F3E0",         // Card border
      headingText: "#2e4630",    // Heading text
      labelText: "#516d4f",      // Label text
      detailTag: "#E8F3E0",      // Detail tag background
      valueText: "#2e4630",      // Value text
      borderTop: "#E8F3E0",      // Border top
      toggleIconCollapsed: "#516d4f", // Toggle icon collapsed
      toggleBgCollapsed: "#E8F3E0",   // Toggle background collapsed
      toggleIconExpanded: "#2e4630",  // Toggle icon expanded
      viewIconColor: "#516d4f",       // View icon
      claimBg: "#344b35",             // Claim button
      claimText: "#ffffff",           // Claim text
      claimHover: "#2a3c2a",          // Claim hover
    },
    
    // ResultView
    resultView: {
      headingText: "#344b35",    // Heading text
      errorText: "#c74f4f",      // Error text
      noMatchText: "#516d4f",    // No match text
    },
    
    // SearchTips
    searchTips: {
      iconColor: "#A0C49D",      // Lightbulb icon
      textColor: "#526D50",      // Tip text
    },
    
    // Sidebar (User)
    sidebarUser: {
      bg: "#FBFFE9",             // Sidebar background
      shadow: "rgba(81, 109, 79, 0.12)", // Box shadow
      logoText: "#516d4f",       // Logo text
      sectionText: "#516d4f",    // Section text
      itemSelected: "rgba(81, 109, 79, 0.08)", // Selected item
      itemHover: "rgba(81, 109, 79, 0.12)",    // Hover item
      iconColor: "#516d4f",      // Icon color
      profileBg: "#e1ecc8",      // Profile button background (active)
      profileBgDefault: "#ffffff", // Profile button default
      profileShadow: "rgba(81, 109, 79, 0.4)", // Profile shadow
      profileShadowAlt: "rgba(81, 109, 79, 0.3)", // Profile shadow alt
      profileHoverBg: "#e1ecc8", // Profile hover background
      profileHoverShadow: "rgba(81, 109, 79, 0.5)", // Profile hover shadow
      profileHoverShadowAlt: "rgba(81, 109, 79, 0.4)", // Profile hover shadow alt
      avatarShadow: "rgba(81, 109, 79, 0.3)", // Avatar shadow
      nameText: "#516d4f",       // Name text
    },
    
    // OverlayModal
    overlayModal: {
      overlayBg: "rgba(0,0,0,0.25)", // Overlay background
    },
    
    // Blob (decorative component)
    blob: {
      fillColor: "#A0C49D",      // Blob fill color (green)
    },
  },
};

// Accent color options (for Settings selector)
const accentColors = {
  blue: {
    main: "#1976d2",
    light: "#63a4ff",
    dark: "#004ba0",
    contrastText: "#fff"
  },
  green: {
    main: "#388e3c",
    light: "#6abf69",
    dark: "#00600f",
    contrastText: "#fff"
  },
  red: {
    main: "#d32f2f",
    light: "#ff6659",
    dark: "#9a0007",
    contrastText: "#fff"
  },
  purple: {
    main: "#7b1fa2",
    light: "#ae52d4",
    dark: "#4a0072",
    contrastText: "#fff"
  }
};

export default function getTheme(mode = "system", accent = "blue") {
  let paletteMode = mode;
  if (mode === "system") {
    if (typeof window !== "undefined" && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      paletteMode = "dark";
    } else {
      paletteMode = "light";
    }
  }
  const accentPalette = accentColors[accent] || accentColors.blue;
  return createTheme({
    palette: {
      mode: paletteMode,
      primary: accentPalette,
    },
    typography: {
      fontFamily: "Poppins, Arial, sans-serif",
      h1: { fontWeight: 700 },
      h2: { fontWeight: 600 },
      body1: { fontWeight: 400 },
      button: { fontWeight: 500 },
    },
  });
}
